import { useEffect, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { StatusBadge } from "../components/StatusBadge";
import { SlaBadge } from "../components/SlaBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { PointPickerMap } from "../components/PointPickerMap";
import { TIPOS_INCIDENCIA } from "../lib/constants";
import api from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UserCog, Plus, Users } from "lucide-react";

const COLUMNAS = ["Nueva", "Asignada", "En curso", "Resuelta", "Verificada", "Cerrada"];
const TRANSICIONES = {
  Nueva: ["Asignada", "Rechazada"],
  Asignada: ["En curso", "Rechazada"],
  "En curso": ["Resuelta"],
  Resuelta: ["Verificada", "En curso"],
  Verificada: ["Cerrada"],
};

function NewIncidentDialog({ open, onClose, sectores, encargados, onCreated }) {
  const [tipo, setTipo] = useState(TIPOS_INCIDENCIA[0]);
  const [tematica, setTematica] = useState("");
  const [sectorId, setSectorId] = useState("");
  const [direccion, setDireccion] = useState("");
  const [responsableId, setResponsableId] = useState("");
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTipo(TIPOS_INCIDENCIA[0]);
      setTematica("");
      setSectorId("");
      setDireccion("");
      setResponsableId("");
      setLat(null);
      setLon(null);
    }
  }, [open]);

  const crear = async () => {
    if (!tematica.trim()) {
      toast.error("Describe la incidencia");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/incidents", {
        origen: "manual",
        tipo,
        tematica,
        sector_id: sectorId || null,
        direccion,
        responsable_id: responsableId || null,
        lat,
        lon,
      });
      toast.success(`Incidencia ${data.codigo} creada`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear la incidencia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-incident-dialog">
        <DialogHeader>
          <DialogTitle>Nueva incidencia manual</DialogTitle>
          <DialogDescription>Incidencia interna de inspección, no procedente de IRIS. Haz clic en el mapa para marcar la ubicación (opcional).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="new-incident-tipo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_INCIDENCIA.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Descripción</Label>
            <Textarea value={tematica} onChange={(e) => setTematica(e.target.value)} placeholder="Describe la incidencia..." data-testid="new-incident-tematica-textarea" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Sector</Label>
              <Select value={sectorId} onValueChange={setSectorId}>
                <SelectTrigger data-testid="new-incident-sector-select">
                  <SelectValue placeholder="Auto (por ubicación)" />
                </SelectTrigger>
                <SelectContent>
                  {sectores.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Responsable</Label>
              <Select value={responsableId} onValueChange={setResponsableId}>
                <SelectTrigger data-testid="new-incident-responsable-select">
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  {encargados.map((e) => (
                    <SelectItem key={e._id} value={e._id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Dirección</Label>
            <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} data-testid="new-incident-direccion-input" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Ubicación en el mapa (opcional)</p>
            <PointPickerMap lat={lat} lon={lon} onPick={(la, lo) => { setLat(la); setLon(lo); }} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={crear} disabled={saving} data-testid="save-new-incident-button" className="bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
            {saving ? "Creando..." : "Crear incidencia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickReassign({ incidentId, encargados, onDone }) {
  const [open, setOpen] = useState(false);

  const reasignar = async (encargadoId) => {
    try {
      await api.patch(`/incidents/${incidentId}/asignar`, { responsable_id: encargadoId });
      toast.success("Incidencia reasignada");
      setOpen(false);
      onDone();
    } catch {
      toast.error("No se pudo reasignar");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          data-testid={`quick-reassign-${incidentId}`}
          className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
          title="Reasignación rápida"
        >
          <UserCog className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-1" onClick={(e) => e.stopPropagation()} data-testid="quick-reassign-panel">
        {encargados.length === 0 && <p className="text-xs text-muted-foreground p-2">Sin encargados disponibles</p>}
        {encargados.map((enc) => (
          <button
            key={enc._id}
            onClick={() => reasignar(enc._id)}
            data-testid={`reassign-to-${enc._id}`}
            className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-secondary"
          >
            {enc.name}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [encargados, setEncargados] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [selected, setSelected] = useState(null);
  const [comentario, setComentario] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [autoAsignando, setAutoAsignando] = useState(false);

  const load = () => api.get("/incidents").then((r) => setIncidents(r.data));

  useEffect(() => {
    load();
    api.get("/incidents/encargados-disponibles").then((r) => setEncargados(r.data)).catch(() => {});
    api.get("/gis/sectores").then((r) => setSectores(r.data)).catch(() => {});
  }, []);

  const autoAsignar = async () => {
    setAutoAsignando(true);
    try {
      const { data } = await api.post("/incidents/auto-asignar");
      toast.success(`${data.asignadas} incidencia(s) asignadas automáticamente por cercanía GPS`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo autoasignar");
    } finally {
      setAutoAsignando(false);
    }
  };

  const transicionar = async (estado) => {
    try {
      await api.patch(`/incidents/${selected._id}/estado`, { estado, comentario });
      toast.success(`Incidencia movida a "${estado}"`);
      setComentario("");
      const { data } = await api.get(`/incidents/${selected._id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el estado");
    }
  };

  const subirFoto = async (file, tipo) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tipo", tipo);
    await api.post(`/incidents/${selected._id}/evidencias`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    const { data } = await api.get(`/incidents/${selected._id}`);
    setSelected(data);
    toast.success("Evidencia subida");
  };

  return (
    <AppLayout title="Gestión de incidencias">
      <div className="flex items-center justify-end gap-2 mb-4">
        <Button onClick={autoAsignar} disabled={autoAsignando} variant="outline" data-testid="auto-assign-button" className="gap-2">
          <Users className="h-4 w-4" /> {autoAsignando ? "Asignando..." : "Auto-asignar pendientes"}
        </Button>
        <Button onClick={() => setNewOpen(true)} data-testid="new-incident-button" className="gap-2 bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
          <Plus className="h-4 w-4" /> Nueva incidencia
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="incidents-kanban">
        {COLUMNAS.map((col) => (
          <div key={col} className="space-y-3">
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground px-1">
              {col} ({incidents.filter((i) => i.estado === col).length})
            </p>
            <div className="space-y-2">
              {incidents
                .filter((i) => i.estado === col)
                .map((inc, idx) => (
                  <motion.div
                    key={inc._id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    onClick={() => setSelected(inc)}
                    data-testid={`incident-card-${inc._id}`}
                    className="w-full text-left cursor-pointer"
                  >
                    <Card className="hover:-translate-y-1 hover:shadow-lg transition-transform">
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold">{inc.codigo}</p>
                          <QuickReassign incidentId={inc._id} encargados={encargados} onDone={load} />
                        </div>
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{inc.tematica}</p>
                        <SlaBadge estado={inc.sla_estado} />
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
            </div>
          </div>
        ))}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="incident-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>{selected.codigo}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2">
                  <StatusBadge estado={selected.estado} />
                  <SlaBadge estado={selected.sla_estado} />
                </div>
                <p className="text-sm text-muted-foreground">{selected.tematica}</p>
                <p className="text-xs">{selected.direccion}</p>

                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Evidencias</p>
                  <div className="grid grid-cols-4 gap-2">
                    {(selected.evidencias || []).map((e, i) => (
                      <div key={i} className={`rounded-md bg-muted h-16 flex items-center justify-center text-[9px] uppercase font-bold ${i === 0 ? "col-span-4 h-24" : "col-span-2"}`}>
                        {e.tipo}
                      </div>
                    ))}
                    <label className="col-span-2 h-16 rounded-md border-2 border-dashed border-border flex items-center justify-center text-[10px] cursor-pointer hover:bg-secondary" data-testid="upload-evidencia-antes">
                      + Antes
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && subirFoto(e.target.files[0], "antes")} />
                    </label>
                    <label className="col-span-2 h-16 rounded-md border-2 border-dashed border-border flex items-center justify-center text-[10px] cursor-pointer hover:bg-secondary" data-testid="upload-evidencia-despues">
                      + Después
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && subirFoto(e.target.files[0], "despues")} />
                    </label>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Historial</p>
                  <div className="space-y-3 border-l-2 border-border pl-4">
                    {(selected.historial || []).map((h, i) => (
                      <div key={i} className="relative">
                        <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-valoriza-leaf" />
                        <p className="text-xs font-bold">{h.estado}</p>
                        <p className="text-[11px] text-muted-foreground">{h.usuario_nombre} · {new Date(h.timestamp).toLocaleString("es-ES")}</p>
                        {h.comentario && <p className="text-xs mt-0.5">{h.comentario}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {TRANSICIONES[selected.estado] && (
                  <div className="space-y-2 border-t border-border pt-4">
                    <Textarea placeholder="Comentario de la transición..." value={comentario} onChange={(e) => setComentario(e.target.value)} data-testid="transition-comment-textarea" />
                    <div className="flex gap-2 flex-wrap">
                      {TRANSICIONES[selected.estado].map((next) => (
                        <Button key={next} size="sm" data-testid={`transition-button-${next.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => transicionar(next)} className="bg-valoriza-leaf text-black hover:bg-valoriza-leaf/90">
                          → {next}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <NewIncidentDialog open={newOpen} onClose={() => setNewOpen(false)} sectores={sectores} encargados={encargados} onCreated={load} />
    </AppLayout>
  );
}
